/* eslint-disable sonarjs/no-duplicate-string */
import { TFitGuideSuggestionInfo } from './FitGuide.types';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TAddToCartButtonInstance } from '../AddToCartButton';
import { TWidget } from 'widgets/Widget';

/**
 * @description FitGuideResult implementation
 * @param Widget Base widget for extending
 * @returns Fit Guide Result class
 */
function FitGuideResultClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product/fitguide
     * @class FitGuideResult
     * @augments Widget
     * @classdesc FitGuideResult component displays the calculated fit guide results.
     * Widget manages next actions:
     * 1. Display recommended size and width information
     * 2. Show measurement details (length and width for both feet)
     * 3. Handle product ordering functionality based on size availability
     * 4. Display warnings when recommended size is not available
     * 5. Provide alternative shopping options when size is unavailable
     * @property {string} data-widget - Widget name `fitGuideResult`
     * @property {object} data-ref - Reference elements for displaying results
     *
     * @example <caption>Example of FitGuideResult widget markup</caption>
     * <div data-widget="fitGuideResult">
     *     <div data-ref="product-suggestion">
     *         <!-- Product suggestion content will be populated here -->
     *     </div>
     *     <div data-ref="recommended-size">
     *         <!-- Recommended size display -->
     *     </div>
     *     <div data-ref="recommended-width">
     *         <!-- Recommended width display -->
     *     </div>
     *     <div data-ref="warning-suggestion">
     *         <!-- Warning message when size unavailable -->
     *     </div>
     * </div>
     */
    class FitGuideResult extends Widget {
        /**
         * @description Product object containing stock and other product information
         * @type {object}
         */
        product: { stock?: number } = {};

        /**
         * @description Validates product options (overridden from ProductOptionMixin but not needed here)
         * @param selectedOptions - Array of selected product options
         * @returns Always returns true as validation is not needed in this context
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        validateProductOptions(selectedOptions: Array<Record<string, unknown> | null>): boolean {
            // This method is overridden in ProductOptionMixin but not needed here
            return true;
        }

        /**
         * Checks if any required product attributes (such as size or width) are unselected.
         * If unselected attributes are found, highlights them and displays an appropriate cart message.
         *
         * @returns Returns `true` as we add product variant.
         */
        checkUnselectedAttributes(): boolean {
            return true;
        }

        /**
         * @description Get selected product options
         * @returns Empty array as no options are selected in fit guide context
         */
        getSelectedProductOptions(): Array<Record<string, unknown> | null> {
            return [];
        }

        /**
         * @description Closes fit guide result view
         * @emits FitGuideResult#drawler.cancel event to close the fit guide drawer
         */
        closeFitGuide() {
            this.eventBus().emit('drawer.cancel');
        }

        /**
         * @description Handler for "Add To Cart" button
         * @param addToCartBtn target "Add To Cart" button
         * @listens dom#click
         */
        handleAddToCart(addToCartBtn: TAddToCartButtonInstance) {
            if (!('addToCart' in this && typeof this.addToCart === 'function')) {
                return;
            }

            this.addToCart(addToCartBtn).then(response => {
                const res = response as { addToCartOverlayUrl?: boolean };

                if (!res?.addToCartOverlayUrl) {
                    this.closeFitGuide();
                }

                if (addToCartBtn.data('analytics')) {
                    this.pushAnalytics(addToCartBtn.data('analytics'));
                } else if (addToCartBtn.config?.analytics) {
                    this.pushAnalytics(addToCartBtn.config.analytics as { item_id: ''; item_variant: ''; item_size: ''; item_width: '' });
                }
            });
        }

        /**
         * @description Pushes analytics data for the fit guide result
         * @param analytics Analytics data to be pushed
         * @description Pushes analytics data for the fit guide result
         * @emits gtm.event event with fit guide result analytics
         */
        pushAnalytics(analytics: { item_id?: string; item_variant?: string; item_size?: string; item_width?: string } | undefined) {
            if (!analytics) {
                return;
            }

            this.eventBus().emit('gtm.event', {
                event_name: 'add_to_cart_fg',
                add_to_cart_fg: {
                    feature: 'fit_guide',
                    item_id: analytics.item_id || '',
                    item_variant: analytics.item_variant || '',
                    value_dim: `${analytics.item_size}_${(analytics.item_width || '').toLowerCase()}`
                }
            });
        }

        handleSetReminderClick(button: TRefElementInstance) {
            const modalUrl = button.data<string>('modalUrl');

            if (!modalUrl) {
                return;
            }

            const url = new URL(modalUrl);

            url.searchParams.set('pid', button.data<string>('pid') as string);

            this.eventBus().emit('drawer.cancel');
            this.eventBus().emit('availabilityReminderModal.show', {
                url: url.toString(),
                headerText: button.data<string>('modalTitle'),
                modalSuccessTitle: button.data<string>('modalSuccessTitle')
            });

            const analytics = button.data('analytics') as { item_id?: string; item_variant?: string; item_size?: string; item_width?: string };

            if (analytics) {
                this.eventBus().emit('gtm.event', {
                    event_name: 'open_availability_reminder',
                    open_availability_reminder: {
                        feature: 'fit_guide',
                        item_id: analytics.item_id || '',
                        item_variant: analytics.item_variant || '',
                        value_dim: `${analytics.item_size}_${(analytics.item_width || '').toLowerCase()}`
                    }
                });
            }
        }

        /**
         * @description Populates and displays fit guide suggestion results
         *
         * @param data - The calculated fit guide suggestion data containing:
         *   - content: HTML content for product suggestion
         *   - size: Suggested size object with region-specific sizes and width info
         *   - resultSize: Formatted main size result
         *   - resultAlternativeSize: Formatted alternative size result
         *   - selected: Object containing left/right length/width measurements
         * @param viewMode - The view mode for displaying results, either 'default' or 'cart'
         */
        populateFitGuideSuggestion(data: TFitGuideSuggestionInfo, viewMode: 'default' | 'cart' | 'content') {
            this.setPref('readyToOrder', data.content?.includes('add-to-cart'));
            this.setPref('selectedQuantity', 1);
            this.product = {
                stock: 1
            };

            this.ref('warning-suggestion').hide();
            this.ref('browse-shop').hide();

            this.ref('product-suggestion').setHtml(data.content ?? '');

            const showWarning = Boolean((viewMode === 'default' && data.content?.includes('data-non-existing-variation')) || data.noExistingSize);

            this.ref('warning-suggestion').toggle(showWarning);
            this.ref('suggestion-content').toggle(!showWarning);
            this.ref('size-description').toggle(!showWarning);
            this.ref('product-suggestion').toggle(!showWarning);
            this.ref('browse-shop').toggle(showWarning);
        }

        /**
         * @description Populates the suggested size and measurements in the fit guide result view
         * @param data - The calculated fit guide suggestion data containing:
         *   - content: HTML content for product suggestion
         *   - size: Suggested size object with region-specific sizes and width info
         *   - resultSize: Formatted main size result
         *   - resultAlternativeSize: Formatted alternative size result
         *   - selected: Object containing left/right length/width measurements
         */
        populateSuggestedSize(data: TFitGuideSuggestionInfo) {
            this.ref('recommended-size').setText(`${data.resultSize} | ${data.resultAlternativeSize}`);
            this.ref('recommended-width')
                .removeClass('m-narrow')
                .removeClass('m-regular')
                .addClass(data.size.narrow ? 'm-narrow' : 'm-regular');

            this.ref('left-length').setText(`${data.selected.leftLength} mm`);
            this.ref('right-length').setText(`${data.selected.rightLength} mm`);
            this.ref('left-width').setText(`${data.selected.leftWidth} mm`);
            this.ref('right-width').setText(`${data.selected.rightWidth} mm`);

            if (data.noProductSuggestion) {
                this.ref('back-to-cart').show();
            }

            if (data.showResultsURL) {
                this.ref('show-results').show().attr('href', data.showResultsURL);
            }

            this.ref('self').show();
        }
    }

    return FitGuideResult;
}

export type TFitGuideResult = ReturnType<typeof FitGuideResultClassCreator>;

export type TFitGuideResultInstance = InstanceType<TFitGuideResult>;

export default FitGuideResultClassCreator;
